import { Hash } from 'lucide-react';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { NOTIFICATIONS_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { SlackActionCustomization } from './SlackActionCustomization';
import {
  collectErrors,
  validateRequiredArray,
  validateRequiredTemplate,
} from '../../fieldValidation';
import { validateTemplateChunks } from '../../validation';
import { buildNodeData } from '../../nodeDataUtils';

/** Validation field names for the Slack action. Shared with the UI. */
export const SlackActionFields = {
  RECIPIENT_TEAMS: 'Recipient teams',
  MESSAGE_TEMPLATE: 'Message template',
} as const;

export const SlackActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'slack_action',

  category: NodeCategory.NOTIFICATIONS,

  label: 'Slack',

  hasInput: true,

  getIcon: (size: number) => (
    <Hash size={size} color={NOTIFICATIONS_ICON_COLOR} />
  ),

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    const teamCount = data.slack_action?.recipient_team_ids?.length || 0;
    if (teamCount === 0) return 'To: (no teams selected)';
    return `To: ${teamCount} team${teamCount === 1 ? '' : 's'}`;
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const slack = nodeData.slack_action;
    return collectErrors(
      validateRequiredArray(
        slack?.recipient_team_ids,
        SlackActionFields.RECIPIENT_TEAMS
      ),
      validateRequiredTemplate(
        slack?.message_chunks ?? [],
        SlackActionFields.MESSAGE_TEMPLATE
      ),
      ...validateTemplateChunks(
        slack?.message_chunks ?? [],
        nodeId,
        graph,
        SlackActionFields.MESSAGE_TEMPLATE
      )
    );
  },

  CustomizationComponent: SlackActionCustomization,

  createDefaultNodeData: () =>
    buildNodeData('slack_action', {
      message_chunks: [],
      recipient_team_ids: [],
    }),

  showCustomizePanelOnCreation: true,
};
